import BarChartIcon from '@precis-digital/kurama/icons/BarChartIcon';
import CalendarIcon from '@precis-digital/kurama/icons/CalendarIcon';
import FilterIcon from '@precis-digital/kurama/icons/FilterIcon';
import PlatformsIcon from '@precis-digital/kurama/icons/PlatformsIcon';
import RecommendationsIcon from '@precis-digital/kurama/icons/RecommendationsIcon';
import { type ReactElement } from 'react';
import type { CopilotPageElement } from '../../pageContext/types';
import { isScorecardMetricElementId, parseScorecardMetricElementId } from 'src/home/copilot/scorecardElements';
const PAGE_ELEMENT_ICONS: Record<string, ReactElement> = {
  date_range: <CalendarIcon />,
  filters: <FilterIcon />,
  scorecard_charts: <BarChartIcon />,
  progress_over_time_chart: <BarChartIcon />,
  detailed_table: <PlatformsIcon />,
  recommendations: <RecommendationsIcon />,
  overall_status: <RecommendationsIcon />,
  occurrence_over_time: <BarChartIcon />,
  recommendations_list: <RecommendationsIcon />
};

/**
 * Converts snake_case or camelCase identifiers into title-case labels for display.
 * Used when no registered page element label is available.
 */
export const formatCopilotIdentifierLabel = (identifier: string): string => identifier.replaceAll('_', ' ').replace(/([a-z])([A-Z])/g, '$1 $2').replace(/\b\w/g, letter => letter.toUpperCase());

/**
 * Resolves a human-readable label for a page element id.
 * Falls back to formatted ids and scorecard metric naming when no element is registered.
 */
export const getPageElementLabel = (elementId: string | undefined, elements: CopilotPageElement[] | undefined): string => {
  if (elementId == null) return 'Element';
  const element = elements?.find(item => item.id === elementId);
  if (element != null) return element.label;
  const scorecardMetricId = parseScorecardMetricElementId(elementId);
  if (scorecardMetricId != null) {
    return `${formatCopilotIdentifierLabel(scorecardMetricId)} scorecard`;
  }
  return formatCopilotIdentifierLabel(elementId);
};

/**
 * Returns the Kurama icon for a page element id used in inline tool-call cards.
 * Defaults to a grid icon for unknown or missing element ids.
 */
export const getPageElementIcon = (elementId: string | undefined): ReactElement => {
  if (elementId == null) return <PlatformsIcon />;
  if (isScorecardMetricElementId(elementId) || elementId === 'scorecard_charts') {
    return <BarChartIcon />;
  }
  return PAGE_ELEMENT_ICONS[elementId] ?? <PlatformsIcon />;
};